/**
 * Demo access helpers for the onboarding click-through.
 *
 * The click-through is reachable, in any auth state, via any of:
 *   - the path:        /onboarding            (best for port-forwarding / clean URLs)
 *   - the hash path:   /#/onboarding
 *   - a query trigger: ?onboarding=1          (also accepts true|demo)
 *
 * `?step=<stepKey>` deep-links directly to a screen (e.g. step=personalize).
 * The dev server's SPA history fallback serves index.html for /onboarding, so
 * the path form works over tunnels/forwarded ports where the hash may be dropped.
 */

const hashQueryString = (): string => {
  const hash = window.location.hash || "";
  return hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "";
};

/** Hash-route parameters win over outer query parameters for the same key. */
const getLocationParam = (key: string): string | null => {
  const hashValue = new URLSearchParams(hashQueryString()).get(key);
  return hashValue ?? new URLSearchParams(window.location.search).get(key);
};

/** True when the current location is the /onboarding path (with any base prefix). */
export const isOnboardingPath = (): boolean => {
  if (typeof window === "undefined") return false;
  const pathname = (window.location.pathname || "").replace(/\/+$/, "");
  return pathname === "/onboarding" || pathname.endsWith("/onboarding");
};

export const isOnboardingDemoRoute = (): boolean => {
  if (typeof window === "undefined") return false;

  if (isOnboardingPath()) return true;

  const hashPath = (window.location.hash || "")
    .replace(/^#/, "")
    .split("?")[0]
    .replace(/\/+$/, "");
  if (hashPath === "/onboarding") return true;

  const val = getLocationParam("onboarding");
  return val === "1" || val === "true" || val === "demo";
};

export const getOnboardingStepParam = (): string | null => {
  if (typeof window === "undefined") return null;
  return getLocationParam("step");
};
